import { PublicClientApplication } from '@azure/msal-browser';
// Resolved through the package exports map, which webpack 5 honors and the
// eslint node resolver does not.
// eslint-disable-next-line import/no-unresolved
import { broadcastResponseToMainFrame } from '@azure/msal-browser/redirect-bridge';

import logger from 'utils/logger';
import { MSAL_REDIRECT_PATH } from './msalRedirect';

// The stage's Microsoft Entra app (client) id. Public, baked in at build time
// like the Google client id. Unset in e2e and in local builds that have not
// configured it — the Microsoft button then simply does not render.
export const MICROSOFT_CLIENT_ID = process.env.REACT_APP_MICROSOFT_CLIENT_ID;

export const isMicrosoftLoginConfigured = () => Boolean(MICROSOFT_CLIENT_ID);

let instancePromise = null;

// Lazy singleton: MSAL is only initialised on the first click, never at page
// load. The popup lands on `/login#code=...` (or `#error=...`) on this same
// origin — the CDN serves `public/` under a build path, so the app origin is
// the only stable redirect target. Since msal-browser v4 the opener does not
// read the popup URL; it waits on a BroadcastChannel that the redirect page
// must post to, which handleMsalPopupResponse does from src/index.js.
export async function getMsalInstance() {
    if (!instancePromise) {
        instancePromise = (async () => {
            const instance = new PublicClientApplication({
                auth: {
                    clientId: MICROSOFT_CLIENT_ID,
                    // Work and school accounts from any tenant; the account
                    // picker never offers personal accounts.
                    authority: 'https://login.microsoftonline.com/organizations',
                    redirectUri: `${window.location.origin}${MSAL_REDIRECT_PATH}`,
                    navigateToLoginRequestUrl: false
                },
                cache: {
                    cacheLocation: 'sessionStorage',
                    storeAuthStateInCookie: false
                }
            });
            // Required before any other call since msal-browser v3.
            await instance.initialize();
            return instance;
        })();
    }
    return instancePromise;
}

// Posts the popup's auth response to the opener's pending loginPopup and
// closes the popup. Must run before the router mounts so nothing rewrites the
// hash first. On failure the opener times out and shows its generic error.
export async function handleMsalPopupResponse() {
    try {
        await broadcastResponseToMainFrame();
    } catch (err) {
        logger.warn('auth:microsoft popup response not forwarded', { code: err?.errorCode });
        window.close();
    }
}
